import type { Distance, Pixel, SpatialRectangle } from "@pdfking/math"
import { Abs, BoundingBox, Outline, Round, Sqrt, ToSpatial } from "@pdfking/math"
import { ZERO_RECTANGLE } from "@pdfking/math/constants"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import type { ColorID, PlotMedia as PlotMediaType, PlotShape } from "@pdfking/renderer-api"
import { ConvertPixels, CreateCompletePage } from "@pdfking/renderer-api"
import {
  DeleteDuplicates,
  Items,
  KeyDrop,
  Length,
  Map,
  RecordMerge,
  SortBy,
  Values,
} from "@pdfking/tfunc"

import type { Guide, LazyPage } from "#public"
import type { SpreadSide } from "#public"

import { ObservedMixin } from "../mixins/ObservedMixin"
import { PlotMixin as mixin } from "../mixins/PlotMixin"
import { FormatDistance } from "../utils/FormatDistance"
import { ComputePageBox } from "./PageBox"
import { PlotShapes } from "./PlotShapes"

/** A picked page: the action the click carried, and the box it covers in the
 * page's own coordinates — measured off the DOM rather than read off the
 * action, whose `position` is local to the groups it is nested in. */
type Picked = {
  action: PlotMediaType
  box: SpatialRectangle<Distance>
}

function partition<T>(inputArray: T[], size: number) {
  return Array.from(
    {
      length: inputArray.length - (size - 1),
    },
    (_, index) => inputArray.slice(index, index + size)
  )
}

// The group the overlay draws in: the page's own space, y up from the trim's
// bottom-left. Clicked shapes are measured against it.
const FRAME_REF = "page-frame"

export const PlotPage = CreateRenderFunction({
  computed: {
    // The page's on-screen geometry — element size, the paper rectangle the
    // mask clips to, and the margins that space it from its neighbours. Bleed
    // is baked in and always reserved; the `bleed` toggle and the clamp flags
    // (set by PlotGroup when this page butts against another in a spread) only
    // move the paper edges within that fixed box.
    box() {
      return ComputePageBox(this.page, { bleed: this.bleed, side: this.spread_side }, this.to_px)
    },
    measure_color() {
      return this.get_color("primary" as ColorID, "blue")
    },
    // Lazy shape generation: only expand the page's shapes once it has scrolled
    // into view (this page's own IntersectionObserver latches `seen`). Cached by
    // the reactive computed; re-runs only when the page or active guides change.
    // Gated on `seen`, not `visible`: a page that has been rendered keeps its
    // shapes when it scrolls away, so scrolling back is free. Completed with
    // the context's defaults, as the PDF plot completes them.
    shapes(): PlotShape[] {
      if (!this.seen) return []
      return CreateCompletePage(this.page.get_page(this.active_guides), this.context).shapes
    },
    // The plot's own space is y-up from the trim's bottom-left; the SVG is
    // y-down from the top-left. One flip here, and everything inside is written
    // in the API's coordinates — groups included, since a `matrix()` composes
    // with it the same way a `cm` does in the PDF.
    flip(): string {
      return `matrix(1 0 0 -1 0 ${this.get_h(this.page)})`
    },
    measures() {
      if (Length(this.selected_media)) {
        const { height, width } = this.page
        // Two edges on the same pixel are one: the picked boxes are read back
        // off the DOM, so the same edge reached from two pages differs by dust.
        const pixel = (value: Distance): number => Round(this.to_px(value))
        const xs: Distance[] = SortBy(
          DeleteDuplicates(
            [
              0,
              width,
              ...Map(
                ({ box: { width, x } }: Picked) => [x, x + width],
                Values(this.selected_media)
              ),
            ].flat() as Distance[],
            pixel
          ),
          pixel
        )
        const ys: Distance[] = SortBy(
          DeleteDuplicates(
            [
              0,
              height,
              ...Map(
                ({ box: { height, y } }: Picked) => [y, y + height],
                Values(this.selected_media)
              ),
            ].flat() as Distance[],
            pixel
          ),
          pixel
        )
        const [lx] = SortBy(xs, (x: Distance) => Abs(width / 2 - x))
        const [ly] = SortBy(ys, (y: Distance) => Abs(height / 2 - y))
        return [
          ...Map(
            ([x1, x2]: Distance[]) => BoundingBox<Distance>({ x: x1!, y: ly! }, { x: x2!, y: ly! }),
            partition(xs, 2)
          ),
          ...Map(
            ([y1, y2]: Distance[]) => BoundingBox<Distance>({ x: lx!, y: y1! }, { x: lx!, y: y2! }),
            partition(ys, 2)
          ),
        ]
      }
      return [] as SpatialRectangle<Distance>[]
    },
    scale_factor() {
      const { height, width } = this.page
      return (width + height) / 150000
    },
  },
  data: {
    selected_media: {} as Record<string, Picked>,
  },
  methods: {
    get_diagonal(position: SpatialRectangle<Distance>): Pixel {
      const { height, width } = this.get_measures(position)
      return Sqrt(width ** 2 + height ** 2) as Pixel
    },
    get_ratio(v: number, opposite?: boolean) {
      const r = 0.5
      return opposite ? v * r : v * (1 - r)
    },
    /** Where a measure's label sits, with its glyphs the right way up.
     *
     * The overlay draws in the page's y-up space, so everything in it is
     * mirrored vertically — which a rectangle survives and a digit does not.
     * The flip is undone about the label's own point, exactly as a plotted text
     * does it, so the label stays where the measure put it and only its glyphs
     * turn back over. */
    get_label_transform(position: SpatialRectangle<Distance>, offset_x: number, offset_y: number) {
      const { x, y } = this.get_measures(position)
      return this.unflip(x + offset_x, y + offset_y)
    },
    get_verbose_distance(position: SpatialRectangle<Distance>) {
      const [m, dec] = FormatDistance(this.get_diagonal(position), "px", 2).split(".")
      const f = this.scale_factor * 3.5
      if (dec) {
        return `<tspan font-size="${f}" dominant-baseline="middle">${m}</tspan><tspan font-size="${f * 0.7} " dominant-baseline="middle">.${dec}</tspan>`
      }
      return `<tspan font-size="${f}" dominant-baseline="middle">${m}</tspan>`
    },
    handle_media_click(page_id: string, page: PlotMediaType, element: SVGGraphicsElement) {
      if (this.selected_media[page_id]) {
        this.selected_media = KeyDrop(this.selected_media, [page_id])
      } else {
        const box = this.measure_media(element)
        if (box) {
          this.selected_media = RecordMerge(this.selected_media, {
            [page_id]: { action: page, box },
          })
        }
      }
      this.on_page_clicked?.({
        number: 0,
        page,
      })
    },
    /** Where a clicked shape actually landed, in this page's own coordinates.
     *
     * Shapes draw inside nested `plot_group`s, so a media's `position` is local
     * to whatever turn and offset the tree put it under — on this sheet every
     * one of them reads `0,0`. Composing that chain back by hand is the browser's
     * job, so it is asked instead: the matrix from the shape's space to the
     * page frame's, applied to the four corners. A quarter turn maps a rectangle
     * onto a rectangle, so their bounds are the box itself, not an approximation
     * of it. Pixels go back to distances on the way out, which is the space
     * everything drawn here is written in. */
    measure_media(element: SVGGraphicsElement): SpatialRectangle<Distance> | undefined {
      const frame = this.root?.querySelector<SVGGraphicsElement>(`[data-ref="${FRAME_REF}"]`)
      const screen = frame?.getScreenCTM()
      const own = element.getScreenCTM()
      if (!screen || !own) return undefined
      const to_page = screen.inverse().multiply(own)
      const { height, width, x, y } = element.getBBox()
      const corners = [
        new DOMPoint(x, y),
        new DOMPoint(x + width, y),
        new DOMPoint(x + width, y + height),
        new DOMPoint(x, y + height),
      ].map(corner => corner.matrixTransform(to_page))
      return Outline(
        corners.map(({ x, y }) =>
          ToSpatial(ZERO_RECTANGLE, ConvertPixels(this.context, { x: x as Pixel, y: y as Pixel }))
        )
      )
    },
  },
  mixins: [mixin, ObservedMixin] as const,
  props: {
    active_guides: [] as Guide[],
    allow_selection: false,
    // When off, crop the rendered plot to the trim box; when on, show bleed.
    bleed: false,
    className: "",
    id: "",
    media_count: 0,
    on_page_clicked: undefined as
      | ((info: { number: number; page: PlotMediaType }) => void)
      | undefined,
    page: {} as LazyPage,
    // Where this page sits in its scroll group (set by PlotGroup): a "single"
    // page keeps all its margins; a spread's "left"/"right" half cuts its inner
    // margin (bleed + padding) so the trims meet hard, no bleed across the spine.
    spread_side: "single" as SpreadSide,
    style: "",
  },
  render() {
    return (
      <svg
        className={this.className}
        height={this.box.height}
        id={this.id}
        style={this.style}
        version="1.2"
        viewBox={`${-this.box.margins.left} ${-this.box.margins.top} ${this.box.width} ${this.box.height}`}
        width={this.box.width}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* The paper region — trim, plus bleed and/or spacing on the sides
              that aren't clamped against a neighbour. Always applied, so a
              spread's inner edges stay hard whether or not bleed is shown. */}
          {/* Sized to the paper: left to its default the mask stops a tenth
              of the viewport out, short of a wide bleed. */}
          <mask
            height={this.box.paper.height}
            id={`${this.uuid}-crop`}
            maskUnits="userSpaceOnUse"
            width={this.box.paper.width}
            x={this.box.paper.x}
            y={this.box.paper.y}
          >
            <rect
              fill="white"
              height={this.box.paper.height}
              width={this.box.paper.width}
              x={this.box.paper.x}
              y={this.box.paper.y}
            />
          </mask>
        </defs>
        <g mask={`url(#${this.uuid}-crop)`} transform={this.flip}>
          {/* Only the trim is paper-white; the bleed area shows whatever the
              artwork paints into it (the mask lets it through), backed by the
              plot's own backdrop where the artwork doesn't reach. */}
          <rect fill="white" height={this.get_h(this.page)} width={this.get_w(this.page)} />
          {this.seen && (
            <PlotShapes
              context={this.context}
              media_click={this.allow_selection ? this.handle_media_click : undefined}
              media_count={this.media_count}
              printable={this.printable}
              request_media={this.request_media}
              shapes={this.shapes}
            />
          )}
        </g>
        {this.seen && (
          <g data-ref={FRAME_REF} transform={this.flip}>
            {Length(this.selected_media) > 0 && (
              <g className="pointer-events-none">
                <mask id={this.uuid}>
                  <rect fill="white" height={this.get_h(this.page)} width={this.get_w(this.page)} />
                  {Items(this.selected_media).map(([id, picked]) => (
                    <rect
                      fill="black"
                      height={this.get_h((picked as Picked).box)}
                      key={id}
                      transform={this.transform_coords((picked as Picked).box)}
                      width={this.get_w((picked as Picked).box)}
                    />
                  ))}
                </mask>
                <g className="cursor-pointer" mask={`url(#${this.uuid})`}>
                  <rect
                    fill="rgba(0, 0, 0, 0.4)"
                    height={this.get_h(this.page)}
                    width={this.get_w(this.page)}
                  />
                </g>
                {this.measures.map((position, id: number) => (
                  <g key={`lines-${id}`}>
                    <polyline
                      points={`0,0 ${this.get_w(position)},${this.get_h(position)}`}
                      stroke="white"
                      stroke-width="3"
                      style="vector-effect: non-scaling-stroke"
                      transform={this.transform_coords(position)}
                    />
                    <polyline
                      points={`0,0 ${this.get_w(position)},${this.get_h(position)}`}
                      stroke={this.measure_color}
                      style="vector-effect: non-scaling-stroke"
                      transform={this.transform_coords(position)}
                    />
                    <circle
                      fill={this.measure_color}
                      r={this.scale_factor}
                      stroke="white"
                      transform={this.transform_coords(position)}
                    />
                    <circle
                      fill={this.measure_color}
                      r={this.scale_factor}
                      stroke="white"
                      transform={this.transform_coords(
                        position,
                        this.get_w(position),
                        this.get_h(position)
                      )}
                    />
                  </g>
                ))}
                {this.measures.map((position, id: number) => (
                  <g key={`labels-${id}`}>
                    <circle
                      fill={this.measure_color}
                      r={this.scale_factor * 5}
                      stroke="white"
                      transform={this.transform_coords(
                        position,
                        this.get_ratio(this.get_w(position)),
                        this.get_ratio(this.get_h(position))
                      )}
                    />
                    <text
                      dangerouslySetInnerHTML={{
                        __html: this.get_verbose_distance(position),
                      }}
                      fill="white"
                      font-weight="bold"
                      text-anchor="middle"
                      transform={this.get_label_transform(
                        position,
                        this.get_ratio(this.get_w(position)),
                        this.get_ratio(this.get_h(position))
                      )}
                    />
                  </g>
                ))}
              </g>
            )}
          </g>
        )}
      </svg>
    )
  },
  watch: {
    page() {
      this.selected_media = {}
    },
  },
})
